import React from "react";
import { MapPin, PackageCheck, Wrench } from "lucide-react";
import BrandschutzCard from "../components/common/cards/BrandschutzCard";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const logoUrl = new URL("../../assets/Bild_Brandschutz.jpg", import.meta.url)
  .href;

export default function Main() {
  return (
    <>
      <Helmet>
        <title>
          Professioneller Brandschutz für Ihr Gebäude | Kaminfeger Knapp
        </title>
        <meta
          name="description"
          content="Individuelle Brandschutzlösungen: Beratung, Planung und Umsetzung von Schutzkonzepten, Löschgeräten und Wartung. Sicherheit für Wohn- und Geschäftsbauten."
        />
      </Helmet>

      <main className="kn-page">
        <div className="flex justify-center mt-24">
          <div className="flex flex-col md:flex-row lg:space-x-24 md:space-x-16">
            <img
              src={logoUrl}
              alt="Brandschutz-Beratung durch Kaminfeger Knapp"
              className="w-auto max-w-full h-auto lg:h-[38em] md:h-[32em] sm:h-[26em] h-[16em] p-4 sm:p-6 md:p-8 lg:p-0 rounded-md shadow-md"
            ></img>

            <div className="kn-intro-copy max-w-2xl mx-auto p-4 sm:p-6 md:p-8 lg:py-0">
              <h1 className="kn-h1">Brandschutz - Beratung</h1>

              <p className="kn-lead">
                Als Kaminfeger ist Brandschutz seit jeher Teil meiner täglichen
                Arbeit. Durch die Durchführung regelmäßiger Kontrollen und
                Reinigungen wird der sichere Betrieb Ihrer Feuerungsanlage
                gewährleistet und Brände werden verhindert.
              </p>

              <p className="kn-body">
                Ab sofort biete ich zusätzlich professionelle Beratung, Verkauf
                und Wartung von Feuerlöschern, Löschdecken und Rauchmeldern an –
                alles aus einer Hand.
              </p>

              <p className="kn-body">
                Ich setze auf fluorfreie Feuerlöscher ohne PFAS –
                umweltfreundlich und zukunftssicher, denn in der EU sind diese
                Stoffe bereits verboten.
              </p>

              <p className="kn-body">
                Gerne besichtige ich Ihre Liegenschaft in der Region Buchs SG
                und berate Sie zu geeigneten Löschmitteln und deren optimaler
                Platzierung. Auch die Wartung Ihrer Feuerlöscher übernehme ich –
                empfohlen alle drei Jahre.
              </p>

              <p className="kn-body font-semibold text-knapp-ink">
                Brandschutz, der schützt – für Ihre Sicherheit, die Umwelt und
                klare Vorschriften.
              </p>
            </div>
          </div>
        </div>

        <section className="max-w-6xl mx-auto px-4 py-16 mt-24">
          <h2 className="kn-h2 text-center mb-12">
            Meine Leistungen im Überblick
          </h2>

          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {[
              {
                title: "Beratung vor Ort",
                icon: MapPin,
                points: [
                  "Individuelle Beurteilung Ihrer Liegenschaft inkl. Platzierung und Wahl des richtigen Löschmittels.",
                ],
              },
              {
                title: "Verkauf & Lieferung",
                icon: PackageCheck,
                points: [
                  "Fluorfreie Feuerlöscher, Rauchmelder und Löschdecken – direkt zu Ihnen geliefert.",
                ],
              },
              {
                title: "Wartung",
                icon: Wrench,
                points: [
                  "Pflichtgemässe Kontrolle und Instandhaltung alle drei Jahre – schnell und unkompliziert.",
                ],
              },
            ].map((service, i) => (
              <BrandschutzCard delay={i * 0.2} key={service.title}>
                <div className="flex items-center gap-3 mb-4">
                  <service.icon className="w-6 h-6 text-knapp-accent" />
                  <h3 className="kn-h3">{service.title}</h3>
                </div>
                <ul className="list-disc list-outside pl-5 space-y-1 kn-body">
                  {service.points.map((point, idx) => (
                    <li key={idx}>{point}</li>
                  ))}
                </ul>
              </BrandschutzCard>
            ))}
          </div>
        </section>

        <section className="max-w-4xl mx-auto px-4 py-12">
          <h2 className="kn-h2 mb-6">
            Brandschutz vom Profi – kompetent, umweltbewusst, regional
          </h2>

          <p className="kn-body mb-6">
            Als Kaminfeger ist Brandschutz ein fester Bestandteil meiner Arbeit.
            Ab sofort biete ich umfassende Dienstleistungen in diesem Bereich
            an: individuelle Beratung, Verkauf von Feuerlöschern, Löschdecken
            und Rauchmeldern – sowie deren Wartung.
          </p>

          <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md mb-6">
            <p className="kn-small text-knapp-accent">
              Die von mir angebotenen fluorfreien Feuerlöscher enthalten kein
              PFAS. Diese sogenannten „ewigen Chemikalien“ sind in der EU seit
              2024 verboten – auch in der Schweiz ist ein Verbot absehbar.
            </p>
          </div>

          <p className="kn-body mb-6">
            Gerne besichtige ich Ihre Liegenschaft in der Region Buchs SG und
            berate Sie zur optimalen Auswahl und Platzierung der Löschmittel.
            Auch die empfohlene Wartung alle drei Jahre übernehme ich
            zuverlässig.
          </p>

          <Link to="/kontakt">
            <button className="kn-btn-primary">Jetzt Angebot anfordern</button>
          </Link>
        </section>
      </main>
    </>
  );
}
